import { useState, useCallback } from 'react';

/**
 * Custom hook for managing form field state with validation
 * @param {string} initialValue - Initial value for the field
 * @param {function} validator - Optional validator function
 * @returns {object} Field state and handlers
 */
const useFormField = (initialValue = '', validator = null) => {
  const [field, setField] = useState({
    value: initialValue,
    error: '',
  });

  const setValue = useCallback((value) => {
    setField((prev) => ({
      ...prev,
      value,
      error: '', // Clear error when value changes
    }));
  }, []);

  const setError = useCallback((error) => {
    setField((prev) => ({
      ...prev,
      error,
    }));
  }, []);

  const validate = useCallback(() => {
    if (validator) {
      const error = validator(field.value);
      if (error) {
        setField((prev) => ({
          ...prev,
          error,
        }));
        return false;
      }
    }
    return true;
  }, [field.value, validator]);

  const reset = useCallback(() => {
    setField({
      value: initialValue,
      error: '',
    });
  }, [initialValue]);

  const clear = useCallback(() => {
    setField({
      value: '',
      error: '',
    });
  }, []);

  return {
    field,
    value: field.value,
    error: field.error,
    hasError: !!field.error,
    setValue,
    setError,
    validate,
    reset,
    clear,
    // For backward compatibility with existing components
    setField,
  };
};

export default useFormField;
